'use client';

/**
 * On-chain Deposit-Protection revoke.
 *
 * "Pause" (`/disable`) only stops the keeper server-side — the signed
 * delegation stays redeemable onchain until it expires. "Revoke" is the
 * permanent onchain kill the user signs:
 *
 *   - 7710-direct (Rabby / Coinbase / Frame): the holder signs
 *     `DelegationManager.disableDelegation(delegation)`, after which the
 *     DM rejects that delegation hash forever. We rebuild the exact
 *     `Delegation` struct from the holder's own (auth-gated) row and
 *     refuse to send unless it hashes back to the stored `delegationHash`
 *     — so a stale/garbled struct can never disable the wrong delegation.
 *   - 7715-grant (MetaMask): a grant is itself an MMDF delegation, so the
 *     holder kills it the SAME way — `DelegationManager.disableDelegation`
 *     on the delegation(s) decoded from the grant blob(s). This is a real,
 *     reliable on-chain revoke: it does NOT depend on MetaMask's
 *     `wallet_revokePermissions` RPC (which MM doesn't expose), and it
 *     works even when the granting account was never upgraded to a smart
 *     account. The on-chain `msg.sender == delegator` check guarantees we
 *     can only disable the holder's own delegations.
 *
 * Either way we then POST `/api/protection/revoke` (with the tx hash) so
 * the server flips the row to `revoked` and stops discovery.
 */

import { useCallback, useState } from 'react';
import { encodeFunctionData, type Address, type Hex } from 'viem';
import { useWallets } from '@privy-io/react-auth';
import {
  DISABLE_DELEGATION_ABI,
  computeDelegationHash,
  computeDelegationStructHash,
  decodeDelegationChain,
  toDelegationStruct,
  type Caveat,
  type DelegationFields,
} from '@takeover/sdk/contracts';
import { useWallet } from './use-wallet';
import { useRevokeDepositProtection } from './use-deposit-protection';

/**
 * What the revoke actually accomplished, so the dialog can be honest:
 *   - `onchain`     a real on-chain `disableDelegation` tx ran (both rails).
 *   - `server-only` nothing was live to disable on-chain; we just settled
 *                   server state.
 */
export type OnchainRevokeOutcome = 'onchain' | 'server-only';

interface ActiveDelegationResponse {
  status: 'active' | 'off';
  signingPath?: '7710-direct' | '7715-grant';
  delegationHash?: Hex;
  delegate?: Address;
  authority?: Hex;
  salt?: string;
  caveats?: Array<{ enforcer: Address; terms: Hex; args?: Hex }>;
  signature?: Hex;
  grants?: Array<{ permissionContext?: Hex }>;
}

export function useOnchainRevoke(): {
  revokeOnchain: () => Promise<OnchainRevokeOutcome>;
  isPending: boolean;
} {
  const { address } = useWallet();
  const { wallets } = useWallets();
  const { revoke } = useRevokeDepositProtection();
  const [isPending, setIsPending] = useState(false);

  const revokeOnchain = useCallback(async (): Promise<OnchainRevokeOutcome> => {
    if (!address) throw new Error('No connected wallet');
    setIsPending(true);
    try {
      const res = await fetch(`/api/protection/delegation?address=${address}`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
          (err as { error?: string })?.error ?? `Could not load delegation (${res.status})`,
        );
      }
      const d = (await res.json()) as ActiveDelegationResponse;
      if (d.status !== 'active') {
        // Nothing live to disable onchain; just settle server state.
        await revoke();
        return 'server-only';
      }

      // Both rails kill the delegation the same way: the holder sends
      // `DelegationManager.disableDelegation(delegation)`, after which the
      // DM rejects that delegation hash forever. They differ only in how
      // we rebuild the struct(s) to disable.
      const chainId = Number(process.env.NEXT_PUBLIC_DEPLOYED_CHAIN_ID ?? '8453');
      const dm = (process.env.NEXT_PUBLIC_DELEGATION_MANAGER ?? '') as Address;
      if (!dm) throw new Error('DelegationManager address is not configured');

      type DisableStruct = ReturnType<typeof toDelegationStruct>;
      const structs: DisableStruct[] = [];

      if (d.signingPath === '7715-grant') {
        // A 7715 grant is an MMDF delegation too. Each grant blob is
        // `abi.encode(Delegation[])`; decode it and disable the leaf the
        // holder signed (delegator == holder). No MetaMask revoke RPC is
        // involved — the DM disable works even when the granting account
        // was never upgraded to a smart account. The on-chain
        // `msg.sender == delegator` check is the final safety net.
        for (const g of d.grants ?? []) {
          if (!g.permissionContext) continue;
          let chain: DelegationFields[];
          try {
            chain = decodeDelegationChain(g.permissionContext);
          } catch {
            continue; // skip a malformed blob rather than aborting the revoke
          }
          for (const leaf of chain) {
            if (leaf.delegator.toLowerCase() === address.toLowerCase()) {
              structs.push(toDelegationStruct(leaf));
            }
          }
        }
        if (structs.length === 0) {
          // No disable-able grant found (shouldn't happen for a live grant).
          // Settle server state so the keeper stops, and report honestly.
          await revoke();
          return 'server-only';
        }
      } else {
        // 7710-direct: rebuild the single struct and verify it hashes back
        // to the stored `delegationHash` before sending — so a stale/garbled
        // row can never disable the wrong delegation (a silent no-op revoke).
        if (!d.delegate || !d.authority || d.salt === undefined || !d.signature || !d.delegationHash) {
          throw new Error('Delegation is missing fields needed to revoke onchain');
        }
        const caveats: Caveat[] = (d.caveats ?? []).map((c) => ({
          enforcer: c.enforcer,
          terms: c.terms,
          args: c.args ?? '0x',
        }));
        const struct: DisableStruct = {
          delegate: d.delegate,
          delegator: address as Address,
          authority: d.authority,
          caveats,
          salt: BigInt(d.salt),
          signature: d.signature,
        };
        const fields = {
          delegate: struct.delegate,
          delegator: struct.delegator,
          authority: struct.authority,
          caveats: struct.caveats,
          salt: struct.salt,
        };
        // The server now stores the DM STRUCT hash (the delegation's
        // onchain identity); rows written before the backfill hold the
        // legacy EIP-712 digest. Accept either so a half-migrated row
        // can still be revoked — both derive from the same fields, so
        // the integrity guarantee is identical.
        const rebuiltStruct = computeDelegationStructHash(fields);
        const rebuiltDigest = computeDelegationHash(chainId, dm, fields);
        const stored = d.delegationHash.toLowerCase();
        if (
          rebuiltStruct.toLowerCase() !== stored &&
          rebuiltDigest.toLowerCase() !== stored
        ) {
          throw new Error(
            'Could not verify the delegation to revoke — nothing was changed. Refresh and try again.',
          );
        }
        structs.push(struct);
      }

      const active = wallets.find((w) => w.address?.toLowerCase() === address.toLowerCase());
      if (!active) throw new Error('Active wallet not found');
      const provider = (await active.getEthereumProvider()) as {
        request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
      };

      // One disable tx per delegation. A 7715 grant covering both USDC and
      // TAKEOVER has two delegations → two prompts; the common USDC-only
      // case is a single prompt, same as 7710.
      let lastTx: Hex | undefined;
      for (const struct of structs) {
        const data = encodeFunctionData({
          abi: DISABLE_DELEGATION_ABI,
          functionName: 'disableDelegation',
          args: [struct],
        });
        lastTx = (await provider.request({
          method: 'eth_sendTransaction',
          params: [{ from: address, to: dm, data }],
        })) as Hex;
      }

      await revoke(lastTx ? { txHash: lastTx } : undefined);
      return 'onchain';
    } finally {
      setIsPending(false);
    }
  }, [address, wallets, revoke]);

  return { revokeOnchain, isPending };
}
